import { useEffect, useState } from "react";
import { Flame } from "lucide-react";

const STORAGE_KEY = "limited_edition_state_v3";
const INITIAL_MS = 25 * 60_000;        // 25 min countdown
const SOLD_OUT_MS = 12 * 60 * 60_000;  // sold out for 12 hours

type Phase = "live" | "soldout";
interface State { phase: Phase; until: number; }

const readState = (): State => {
  if (typeof window === "undefined") return { phase: "live", until: Date.now() + INITIAL_MS };
  const now = Date.now();
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      const s = JSON.parse(raw) as State;
      // advance phases as needed if user has been away
      let phase = s.phase, until = s.until;
      while (until <= now) {
        if (phase === "live") { phase = "soldout"; until += SOLD_OUT_MS; }
        else { phase = "live"; until += INITIAL_MS; }
      }
      const next = { phase, until };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    } catch { /* fall through */ }
  }
  const init: State = { phase: "live", until: now + INITIAL_MS };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(init));
  return init;
};

const computeLeft = (until: number) => {
  const diff = Math.max(0, until - Date.now());
  return {
    hours: Math.floor(diff / 3_600_000),
    minutes: Math.floor((diff / 60_000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    diff,
  };
};

interface Props {
  onExpired?: (expired: boolean) => void;
}

export const CountdownTimer = ({ onExpired }: Props) => {
  const [state, setState] = useState<State>(() => readState());
  const [time, setTime] = useState(() => computeLeft(state.until));

  useEffect(() => {
    const tick = () => {
      const left = computeLeft(state.until);
      setTime(left);
      if (left.diff <= 0) {
        // flip phase
        const next: State = state.phase === "live"
          ? { phase: "soldout", until: Date.now() + SOLD_OUT_MS }
          : { phase: "live", until: Date.now() + INITIAL_MS };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        setState(next);
      }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [state]);

  // notify consumer of sold-out phase
  useEffect(() => {
    onExpired?.(state.phase === "soldout");
  }, [state.phase, onExpired]);

  const pad = (n: number) => n.toString().padStart(2, "0");
  const isSoldOut = state.phase === "soldout";

  if (isSoldOut) {
    return (
      <div
        className="flex items-center gap-2 font-display text-sm md:text-base tracking-widest"
        aria-live="polite"
      >
        <Flame className="w-4 h-4 text-accent animate-[flame-flicker_0.8s_ease-in-out_infinite]" />
        <span className="text-accent">SOLD OUT</span>
        <span className="bg-accent text-accent-foreground px-3 py-1 shadow-[0_0_12px_hsl(var(--accent)/0.6)]">
          WE'LL NOTIFY YOU WHEN AVAILABLE
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 font-display text-sm md:text-base tracking-widest animate-[urgency-pulse_1.2s_ease-in-out_infinite]"
      aria-live="polite"
    >
      <Flame className="w-4 h-4 text-accent animate-[flame-flicker_0.8s_ease-in-out_infinite]" />
      <span className="text-accent">HURRY — ENDS IN</span>
      <span className="bg-accent text-accent-foreground px-2.5 py-1 tabular-nums shadow-[0_0_12px_hsl(var(--accent)/0.6)]">{pad(time.hours)}</span>
      <span className="text-accent animate-pulse">:</span>
      <span className="bg-accent text-accent-foreground px-2.5 py-1 tabular-nums shadow-[0_0_12px_hsl(var(--accent)/0.6)]">{pad(time.minutes)}</span>
      <span className="text-accent animate-pulse">:</span>
      <span className="bg-accent text-accent-foreground px-2.5 py-1 tabular-nums shadow-[0_0_12px_hsl(var(--accent)/0.6)]">{pad(time.seconds)}</span>
    </div>
  );
};